import type { MenuItemName } from 'app/components/pages/Packs/Common/menuItem';
import { PACK_TAB_FIELDS } from 'app/components/pages/Packs/Common/packTabFields';
import type { PackExperienceResponse } from 'app/typings/core/admin/packs/pack';
import type { UpdatePackRequest } from 'app/typings/core/admin/packs/packForm';
import type { PackageDisclaimer } from 'app/typings/disclaimers';
import { format } from 'app/utils/dayjs';
import type { Dayjs } from 'app/utils/dayjs';

export const mapPackToUpdateRequest = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic form values with mixed shapes
  values: Record<string, any>,
  activeTab?: MenuItemName
): UpdatePackRequest => {
  const allFields = createExhaustiveFieldsList({
    nameForHotel: true,
    nameForUser: true,
    channelManagerRateCode: true,
    openingHours: true,
    packRooms: true,
    packExperiences: true,
    minChargedPax: true,
    maxAllowedPax: true,
    maxAllowedAdult: true,
    maxAllowedChild: true,
    maxBookingConfiguration: true,
    description: true,
    isGem: true,
    oldPackId: true,
    ownerId: true,
    horizontalCoverPicture: true,
    verticalCoverPicture: true,
    introPictures: true,
    experiences: true,
    customTagIds: true,
    disclaimers: true,
  });

  const allowedFields = activeTab ? PACK_TAB_FIELDS[activeTab] : null;

  const isFieldAllowed = (field: string) =>
    allowedFields === null || allowedFields.includes(field);

  const basePayload = Object.fromEntries(
    allFields
      .filter((field) => field in values && isFieldAllowed(field))
      .map((field) => {
        if (field === 'openingHours' && values.openingHours) {
          return [field, values.openingHours];
        }

        if (field === 'maxBookingConfiguration') {
          if (values.maxBookingConfiguration?.type === 'none') {
            return [field, null];
          }

          return [field, values.maxBookingConfiguration];
        }

        const value = values[field as keyof typeof values];

        return [field, value === '' ? null : value];
      })
  );

  if (values.isDaytime && 'channelManagerRateCode' in basePayload) {
    basePayload.channelManagerRateCode = null;
  }

  if (Array.isArray(basePayload.packExperiences)) {
    basePayload.packExperiences = (
      basePayload.packExperiences as PackExperienceResponse[]
    ).map((exp) => ({
      ...exp,
      isPerPerson: Boolean(exp.isPerPerson),
      isPerNight: Boolean(exp.isPerNight),
      isOptional: !exp.isIncluded || Boolean(exp.isOptional),
      isIncluded: Boolean(exp.isIncluded),
    }));
  }

  if (values.aliasForHotel && isFieldAllowed('aliasForHotel')) {
    basePayload.nameForHotel = values.aliasForHotel;
  }

  if (values.aliasForUser && isFieldAllowed('aliasForUser')) {
    basePayload.nameForUser = values.aliasForUser;
  }

  if (
    values.horizontalCoverPicture?.length > 0 &&
    isFieldAllowed('horizontalCoverPicture')
  ) {
    basePayload.horizontalCoverPicture = values.horizontalCoverPicture[0];
  }

  if (
    values.verticalCoverPicture?.length > 0 &&
    isFieldAllowed('verticalCoverPicture')
  ) {
    basePayload.verticalCoverPicture = values.verticalCoverPicture[0];
  }

  if (values.channelManageRateCode && isFieldAllowed('channelManageRateCode')) {
    basePayload.channelManageRateCode = values.channelManageRateCode;
  }

  if (values.disclaimers && isFieldAllowed('disclaimers')) {
    basePayload.disclaimers = values.disclaimers.map(
      (
        disclaimer: PackageDisclaimer & {
          dateRange?: [Dayjs | null, Dayjs | null];
        },
        index: number
      ) => ({
        ...disclaimer,
        startDate: disclaimer.dateRange?.[0]
          ? format(disclaimer.dateRange[0], 'YYYY-MM-DD')
          : format(new Date(), 'YYYY-MM-DD'),
        endDate: disclaimer.dateRange?.[1]
          ? format(disclaimer.dateRange[1], 'YYYY-MM-DD')
          : null,
        priority: index + 1,
      })
    );
  }

  return basePayload as UpdatePackRequest;
};

const createExhaustiveFieldsList = <
  T extends Record<keyof UpdatePackRequest, true>
>(
  fields: T
): (keyof T)[] => Object.keys(fields) as (keyof T)[];
